<template>
  <div class="visit-plan-estim-date-form">
    <h4 class="global-h4 visit-plan-estim-date-form__title">When will your Visit Plan be ready?</h4>

    <div class="visit-plan-estim-date-form__description">
      A Visit Plan lists out the locations and activities for your visit.<br />
      It's an important step to ensure you and the destination are on the same page before you arrive!<br />
      Typically due about ~{{ AverageDaysToDeliverPlan }} days after confirming (can be updated any time).<br />
    </div>

    <input
      :class="{
        'global-text-input': true,
        'global-text-input--error': props.isInputErrorVisible || isDateInvalid,
        'visit-plan-estim-date-form__date-picker-input': true,
      }"
      type="date"
      :min="moment().format('YYYY-MM-DD')"
      :max="maxAllowedDate"
      v-model="_estimDate"
      @change="emit('update:estimDate', _estimDate)"
    />

    <div v-if="isDateInvalid && _estimDate" class="global-error-string visit-plan-estim-date-form__validation-error">
      {{ validationErrorMessage }}
    </div>

    <NoteWithIcon v-if="isLateCreatorChoiceWarningVisible" class="visit-plan-estim-date-form__warning-note" color="yellow" size="small">
      <template #text>
        <h6 class="global-h6">It looks like this creator visit was set up last minute, please submit your Visit Plan as soon as possible!</h6>
      </template>
    </NoteWithIcon>

    <template v-else>
      <textarea
        v-if="isLightWarningVisible || isAggressiveWarningVisible"
        class="global-textarea visit-plan-estim-date-form__explanation-textarea"
        placeholder="Tell us why the Visit Plan won't be ready in the typical time frame."
        v-model="_estimNotes"
        @input="emit('update:estimNotes', _estimNotes)"
      ></textarea>

      <NoteWithIcon v-if="isAggressiveWarningVisible" class="visit-plan-estim-date-form__warning-note" color="red" size="small">
        <template #text>
          <div>
            <h6 class="global-h6" style="margin-bottom: 5px">Last-minute visit planning increases risk and can make coordination more challenging.</h6>
            Complete your visit plan at least 4 weeks before the visit date.
          </div>
        </template>
      </NoteWithIcon>

      <NoteWithIcon v-else-if="isLightWarningVisible" class="visit-plan-estim-date-form__warning-note" color="yellow" size="small">
        <template #text>
          <h6 class="global-h6">Completing your Visit Plan sooner helps ensure everyone is on the same page and activities can be coordinated in time.</h6>
        </template>
      </NoteWithIcon>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import moment from "moment";
import dayjs from "dayjs";

// Components
import NoteWithIcon from "@components/NoteWithIcon.vue";

const props = withDefaults(
  defineProps<{
    creatorChosenDate: string | null;
    actualTimingDate: string | null;
    estimDate: string | null;
    estimNotes: string | null;
    isInputErrorVisible?: boolean;
  }>(),
  {
    creatorChosenDate: null,
    actualTimingDate: null,
    estimDate: null,
    estimNotes: null,
    isInputErrorVisible: false,
  }
);

const emit = defineEmits<{
  (e: "update:estimDate", newDate: string): void;
  (e: "update:estimNotes", newNote: string): void;
  (e: "update:isValid", isValid: boolean): void;
}>();

// Local estim date and notes =================================================
const _estimDate = ref<string>("");
const _estimNotes = ref<string>("");

// Time frame constants =======================================================
const AverageDaysToDeliverPlan = 10;
const DaysFromNowToShowLightWarning = 14;
const DaysBeforeVisitToShowAggressiveWarning = 28;
const DaysToEvaluateLateCreatorChoice = 28;
const DaysBeforeVisitForPlanDeadline = 3;

// Date validation ============================================================
const maxAllowedDate = computed<string>(() => {
  if (!props.actualTimingDate) return moment().add(100, "years").format("YYYY-MM-DD");

  const threeDeysBeforeVisit = dayjs(props.actualTimingDate).subtract(DaysBeforeVisitForPlanDeadline, "day");
  const today = dayjs();

  // If today is already past the 3-day buffer, don't enforce the buffer restriction
  if (today.isAfter(threeDeysBeforeVisit, "day")) {
    return moment(props.actualTimingDate).format("YYYY-MM-DD");
  }

  return threeDeysBeforeVisit.format("YYYY-MM-DD");
});

const isDateInPast = computed<boolean>(() => {
  if (!_estimDate.value) return false;
  return dayjs(_estimDate.value).isBefore(dayjs(), "day");
});

const isDateTooCloseToVisit = computed<boolean>(() => {
  if (!_estimDate.value || !props.actualTimingDate) return false;

  const threeDeysBeforeVisit = dayjs(props.actualTimingDate).subtract(DaysBeforeVisitForPlanDeadline, "day");
  const today = dayjs();

  // If today is already past the 3-day buffer, don't validate
  if (today.isAfter(threeDeysBeforeVisit, "day")) {
    return false;
  }

  return dayjs(_estimDate.value).isAfter(threeDeysBeforeVisit, "day");
});

const isDateInvalid = computed<boolean>(() => {
  return isDateInPast.value || isDateTooCloseToVisit.value;
});

const validationErrorMessage = computed<string>(() => {
  if (isDateInPast.value) return "Date cannot be in the past";
  if (isDateTooCloseToVisit.value) return "Visit plan must be ready at least 3 days before your visit";
  return "";
});

// Warnings visibility ========================================================
const isLightWarningVisible = computed<boolean>(() => {
  if (!_estimDate.value) return false;

  const differenceInDays = dayjs(_estimDate.value).diff(dayjs(), "day");
  return differenceInDays > DaysFromNowToShowLightWarning;
});

const isAggressiveWarningVisible = computed<boolean>(() => {
  if (!props.actualTimingDate || !_estimDate.value) return false;

  const differenceInDays = dayjs(props.actualTimingDate).diff(dayjs(_estimDate.value), "day");
  return differenceInDays < DaysBeforeVisitToShowAggressiveWarning;
});

// this warning should override the warnings above
const isLateCreatorChoiceWarningVisible = computed<boolean>(() => {
  if (!props.actualTimingDate || !props.creatorChosenDate) return false;

  const differenceInDays = dayjs(props.actualTimingDate).diff(dayjs(props.creatorChosenDate), "day");

  return differenceInDays < DaysToEvaluateLateCreatorChoice;
});

// Lifecycle and watchers =====================================================
onMounted(() => {
  _estimDate.value = props.estimDate;
  _estimNotes.value = props.estimNotes;
  // Emit initial validity state
  emit("update:isValid", !isDateInvalid.value && !!_estimDate.value);
});

// Watch for validity changes and emit to parent
watch(isDateInvalid, newIsInvalid => {
  emit("update:isValid", !newIsInvalid && !!_estimDate.value);
});

watch(_estimDate, newDate => {
  emit("update:isValid", !isDateInvalid.value && !!newDate);
});
</script>

<style scoped lang="scss">
// Visit plan estimated date form =============================================
.visit-plan-estim-date-form {
  &__title {
    margin-bottom: 7px;
  }

  &__description {
    margin-bottom: 22px;
  }

  &__date-picker-input {
    max-width: 150px;
    margin-bottom: 15px;
  }

  &__warning-note {
    margin-bottom: 15px;
  }

  &__explanation-link {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__explanation-textarea {
    height: 70px;
    min-height: 70px;
    margin-bottom: 12px;
  }

  &__validation-error {
    margin-top: 5px;
    margin-bottom: 10px;
  }
}
</style>
